import { RefObject, useLayoutEffect } from 'react';

/**
 * Auto-grow a textarea up to `maxRows`, then scroll internally. `useLayoutEffect`
 * avoids a one-frame paint flash after each keystroke. Reads `line-height` from
 * computed styles (falls back to 24px) so any Tailwind `leading-*` utility works.
 */
export function useAutoGrowTextarea(
  ref: RefObject<HTMLTextAreaElement | null>,
  value: string,
  maxRows: number = 4,
): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Collapse to natural single-row height first so scrollHeight reflects
    // the content's true required height (and not the stale prior height).
    el.style.height = 'auto';
    const computed = window.getComputedStyle(el);
    const parsedLineHeight = parseFloat(computed.lineHeight);
    const lineHeight = Number.isFinite(parsedLineHeight) && parsedLineHeight > 0 ? parsedLineHeight : 24;
    const maxHeight = lineHeight * maxRows;
    const next = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [ref, value, maxRows]);
}
